/**
 * @description Hook to manage infinite scrolling behavior for a slider.
 * @param {T[]} items - The items to display in the slider.
 * @returns {Object} An object containing the container reference, the duplicated items, and the x motion value.
*/

import {
  useRef, useEffect, useCallback, useState,
} from "react";
import { useMotionValue, animate } from "motion/react";

const DUPLICATION_COUNT = 5;
const GAP_PX = 12;

const calculateSingleSetWidth = (
  container: HTMLDivElement,
  itemsLength: number,
): number => {
  const children = container.children;
  if (children.length === 0) return 0;

  const firstChild = children[0] as HTMLElement;
  const itemWidth = firstChild.offsetWidth;
  return (itemWidth + GAP_PX) * itemsLength;
};

type UseInfiniteSliderOptions = { currentIndex?: number };

export const useInfiniteSlider = <T>(items: T[], options: UseInfiniteSliderOptions = {}) => {
  const { currentIndex } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const previousIndexRef = useRef<number | undefined>(undefined);
  const x = useMotionValue(0);
  const duplicatedItems = Array(DUPLICATION_COUNT).fill(items).flat();
  const [isDragging, setIsDragging] = useState(false);
  const dragEndTimeRef = useRef<number | null>(null);

  const scrollToIndex = useCallback((index: number, wrapDirection: "next" | "prev" | null = null) => {
    if (!containerRef.current || items.length === 0) return;

    const container = containerRef.current;
    const children = container.children;
    if (children.length === 0) return;

    const firstChild = children[0] as HTMLElement;
    const itemWidth = firstChild.offsetWidth;
    const singleSetWidth = (itemWidth + GAP_PX) * items.length;
    const containerWidth = container.parentElement?.offsetWidth ?? 0;
    const centerOffset = (containerWidth - itemWidth) / 2;

    const baseSetIndex = 2;
    const baseTargetX = -singleSetWidth * baseSetIndex - (itemWidth + GAP_PX) * index + centerOffset;

    if (wrapDirection === "next") {
      const nextSetTargetX = -singleSetWidth * (baseSetIndex + 1) + centerOffset;
      animate(x, nextSetTargetX, {
        duration: 0.3,
        ease: "easeOut",
        onComplete: () => x.set(baseTargetX),
      });
    }
    else if (wrapDirection === "prev") {
      const prevSetTargetX = -singleSetWidth * (baseSetIndex - 1) - (itemWidth + GAP_PX) * (items.length - 1) + centerOffset;
      animate(x, prevSetTargetX, {
        duration: 0.3,
        ease: "easeOut",
        onComplete: () => x.set(baseTargetX),
      });
    }
    else {
      animate(x, baseTargetX, { duration: 0.3, ease: "easeOut" });
    }
  }, [items.length, x]);

  useEffect(() => {
    if (!containerRef.current) return;

    const initializePosition = () => {
      if (!containerRef.current || isDragging) return;

      const now = Date.now();
      if (dragEndTimeRef.current && now - dragEndTimeRef.current < 500) return;

      const singleSetWidth = calculateSingleSetWidth(
        containerRef.current,
        items.length,
      );

      if (singleSetWidth > 0) {
        const currentX = x.get();
        if (currentX === 0) {
          x.set(-singleSetWidth * 2);
        }
      }
    };

    const handleScroll = () => {
      if (isDragging) return;

      const now = Date.now();
      if (dragEndTimeRef.current && now - dragEndTimeRef.current < 500) return;

      const currentX = x.get();

      if (!containerRef.current) return;

      const singleSetWidth = calculateSingleSetWidth(
        containerRef.current,
        items.length,
      );

      if (singleSetWidth === 0) return;

      const minX = -singleSetWidth * (DUPLICATION_COUNT - 1);
      const maxX = 0;
      const tolerance = 1;

      if (currentX > maxX + tolerance) {
        x.jump(currentX - singleSetWidth);
      }
      else if (currentX < minX - tolerance) {
        x.jump(currentX + singleSetWidth);
      }
    };

    const resizeObserver = new ResizeObserver(initializePosition);
    resizeObserver.observe(containerRef.current);

    const unsubscribe = x.on("change", handleScroll);

    const timer = setTimeout(initializePosition, 0);

    return () => {
      resizeObserver.disconnect();
      unsubscribe();
      clearTimeout(timer);
    };
  }, [x, items.length, duplicatedItems.length, isDragging]);

  useEffect(() => {
    if (currentIndex !== undefined && currentIndex >= 0) {
      const prevIndex = previousIndexRef.current;
      let wrapDirection: "next" | "prev" | null = null;

      if (prevIndex !== undefined && items.length > 1) {
        if (prevIndex === items.length - 1 && currentIndex === 0) {
          wrapDirection = "next";
        }
        else if (prevIndex === 0 && currentIndex === items.length - 1) {
          wrapDirection = "prev";
        }
      }

      const timer = setTimeout(() => scrollToIndex(currentIndex, wrapDirection), 50);
      previousIndexRef.current = currentIndex;
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentIndex, scrollToIndex, items.length]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDrag = () => {
    if (!containerRef.current) return;

    const currentX = x.get();
    const singleSetWidth = calculateSingleSetWidth(
      containerRef.current,
      items.length,
    );

    if (singleSetWidth === 0) return;

    if (currentX > 0) {
      x.jump(currentX - singleSetWidth);
    }
    else if (currentX < -singleSetWidth * (DUPLICATION_COUNT - 1)) {
      x.jump(currentX + singleSetWidth);
    }
  };

  const handleDragEnd = () => {
    dragEndTimeRef.current = Date.now();
    setIsDragging(false);
  };

  return {
    containerRef,
    duplicatedItems,
    x,
    scrollToIndex,
    handleDragStart,
    handleDrag,
    handleDragEnd,
    isDragging,
  };
};
